export const TX_CHANNELS = [
  { value: 'all', label: 'All activity' },
  { value: 'storefront', label: 'Online orders' },
  { value: 'in_store', label: 'In-store (POS)' },
] as const

export type AuditTab = 'tx' | 'orders'
export type TxChannel = (typeof TX_CHANNELS)[number]['value']

export type AuditNavState = {
  storeName?: string
  organisationName?: string
  fromWithdrawalId?: number
}

export type AuditListSearch = {
  tab: AuditTab
  channel: TxChannel
  txPage: number
  ordPage: number
  ordStatus: string
  ordPayment: string
}

const TX_REF_PATTERN = /^(ledger|withdrawal|pos)-\d+$/

export function isValidTransactionRef(ref: string | undefined): ref is string {
  return ref != null && TX_REF_PATTERN.test(ref)
}

export function storeAuditPath(storeId: number | string, search?: URLSearchParams | string): string {
  const base = `/withdrawals/audit/${storeId}`
  if (!search) return base
  const q = typeof search === 'string' ? search : search.toString()
  return q ? `${base}?${q}` : base
}

export function storeAuditTransactionPath(
  storeId: number | string,
  txRef: string,
  search?: URLSearchParams | string,
): string {
  const base = `/withdrawals/audit/${storeId}/transactions/${encodeURIComponent(txRef)}`
  if (!search) return base
  const q = typeof search === 'string' ? search : search.toString()
  return q ? `${base}?${q}` : base
}

function parsePositiveInt(value: string | null, fallback: number): number {
  const n = Number.parseInt(value ?? '', 10)
  return Number.isFinite(n) && n >= 1 ? n : fallback
}

function parseChannel(value: string | null): TxChannel {
  if (value === 'storefront' || value === 'in_store') return value
  return 'all'
}

export function parseAuditListSearch(searchParams: URLSearchParams): AuditListSearch {
  return {
    tab: searchParams.get('tab') === 'orders' ? 'orders' : 'tx',
    channel: parseChannel(searchParams.get('channel')),
    txPage: parsePositiveInt(searchParams.get('txPage'), 1),
    ordPage: parsePositiveInt(searchParams.get('ordPage'), 1),
    ordStatus: searchParams.get('ordStatus') ?? '',
    ordPayment: searchParams.get('ordPayment') ?? '',
  }
}

export function buildAuditListSearch(params: Partial<AuditListSearch>): URLSearchParams {
  const next = new URLSearchParams()
  const tab = params.tab ?? 'tx'
  const channel = params.channel ?? 'all'
  const txPage = params.txPage ?? 1
  const ordPage = params.ordPage ?? 1
  const ordStatus = params.ordStatus ?? ''
  const ordPayment = params.ordPayment ?? ''

  if (tab !== 'tx') next.set('tab', tab)
  if (channel !== 'all') next.set('channel', channel)
  if (txPage > 1) next.set('txPage', String(txPage))
  if (ordPage > 1) next.set('ordPage', String(ordPage))
  if (ordStatus.trim()) next.set('ordStatus', ordStatus.trim())
  if (ordPayment.trim()) next.set('ordPayment', ordPayment.trim())
  return next
}

export function txKindLabel(kind: string) {
  if (kind === 'ledger') return 'Ledger'
  if (kind === 'withdrawal') return 'Withdrawal'
  if (kind === 'pos_sale') return 'POS'
  return kind
}

export function transactionDetailRef(row: { kind: string; payload?: Record<string, unknown> }): string | null {
  const ref = String(row.payload?.detailRef ?? '').trim()
  if (!ref) return null
  return isValidTransactionRef(ref) ? ref : null
}
